import moment from "moment";

const normalizeRecurrenceType = (value) => {
  const type = String(value || "none").toLowerCase().trim();

  if (!type || type === "one-time" || type === "onetime") return "none";
  if (type === "bi-weekly" || type === "bi weekly") return "biweekly";
  if (type === "bi-monthly" || type === "bi monthly") return "bimonthly";
  if (type === "bi-yearly" || type === "bi yearly") return "biyearly";
  if (type === "custom dates" || type === "customdates") return "customdates";

  return type;
};

export const getOccurrencesForJob = (job, rangeStart, rangeEnd) => {
  const recurrence =
    job?.recurrence && typeof job.recurrence === "object"
      ? job.recurrence
      : {
          type:
            job?.recurrence ||
            job?.reccurance ||
            job?.recurring ||
            job?.repeat ||
            "none",
        };

  const type = normalizeRecurrenceType(recurrence?.type);
  const start = job?.startDate
    ? moment(String(job.startDate).slice(0, 10), "YYYY-MM-DD", true).startOf("day")
    : null;

  const windowStart = moment(rangeStart).startOf("day");
  const windowEnd = moment(rangeEnd).startOf("day");

  if (!start || !start.isValid() || !windowStart.isValid() || !windowEnd.isValid()) {
    return [];
  }

  if (type === "none") {
    const key = start.format("YYYY-MM-DD");
    return start.isBetween(windowStart, windowEnd, "day", "[]") ? [key] : [];
  }

  // Same series-end rule as getRecurrenceEndKey in recurringEditUtils.
  const seriesEnd =
    recurrence?.endsOn || recurrence?.endDate || job?.recurrenceEndDate || "";
  const endsOn = seriesEnd
    ? moment(String(seriesEnd).slice(0, 10), "YYYY-MM-DD", true).startOf("day")
    : windowEnd;

  const hardEnd = moment.min(endsOn.isValid() ? endsOn : windowEnd, windowEnd);
  const occurrences = new Set();

  const addIfInWindow = (date) => {
    if (!date?.isValid()) return;
    if (date.isBefore(start, "day")) return;
    if (date.isBefore(windowStart, "day")) return;
    if (date.isAfter(hardEnd, "day")) return;
    occurrences.add(date.format("YYYY-MM-DD"));
  };

  if (type === "customdates") {
    const customDates = Array.isArray(recurrence.customDates)
      ? recurrence.customDates
      : [];

    customDates.forEach((value) => {
      const date = moment(String(value).slice(0, 10), "YYYY-MM-DD", true).startOf("day");
      addIfInWindow(date);
    });

    return Array.from(occurrences).sort();
  }

  if (type === "weekly" || type === "biweekly") {
    const intervalWeeks = type === "biweekly" ? 2 : 1;
    const selectedDays =
      Array.isArray(recurrence.daysOfWeek) && recurrence.daysOfWeek.length
        ? recurrence.daysOfWeek.map(Number).filter((day) => day >= 0 && day <= 6)
        : [start.day()];

    let cursor = moment.max(start, windowStart).clone();

    while (cursor.isSameOrBefore(hardEnd, "day")) {
      const weeksDiff = cursor
        .clone()
        .startOf("week")
        .diff(start.clone().startOf("week"), "weeks");

      if (
        weeksDiff >= 0 &&
        weeksDiff % intervalWeeks === 0 &&
        selectedDays.includes(cursor.day())
      ) {
        addIfInWindow(cursor);
      }

      cursor.add(1, "day");
    }

    return Array.from(occurrences).sort();
  }

  const increments = {
    daily: [1, "day"],
    monthly: [1, "month"],
    bimonthly: [2, "months"],
    quarterly: [3, "months"],
    biyearly: [6, "months"],
    yearly: [1, "year"],
  };

  const increment = increments[type];
  if (!increment) {
    addIfInWindow(start);
    return Array.from(occurrences).sort();
  }

  let cursor = start.clone();
  while (cursor.isBefore(windowStart, "day")) {
    cursor.add(increment[0], increment[1]);
  }

  while (cursor.isSameOrBefore(hardEnd, "day")) {
    addIfInWindow(cursor);
    cursor.add(increment[0], increment[1]);
  }

  return Array.from(occurrences).sort();
};
